import React from 'react';
import { CheckCircle2, ArrowRight, Sparkles, Star } from 'lucide-react';
import { Button } from './ui/button';
import { heroData, trustBadges } from '../mock';

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) {
    const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }
};

export default function Hero() {
  return (
    <section id="home" className="relative overflow-hidden hero-glow">
      <div className="absolute inset-0 grid-lines opacity-60 pointer-events-none"/>
      <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-gradient-to-br from-teal-200/40 to-teal-50 blur-3xl pointer-events-none"/>

      <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-24 lg:pt-24 lg:pb-32">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 fade-up">
            <span className="chip">
              <Sparkles size={12}/> {heroData.eyebrow}
            </span>
            <h1 className="mt-6 text-[44px] md:text-[56px] lg:text-[64px] leading-[1.02] tracking-tight font-bold text-[#0a2540]">
              Fuel Your <span className="font-serif-display italic text-teal-600">business</span> growth today
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              {heroData.subtitle}
            </p>

            <ul className="mt-8 grid sm:grid-cols-2 gap-3 max-w-xl">
              {heroData.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-slate-700">
                  <CheckCircle2 size={20} className="text-teal-500 mt-0.5 flex-shrink-0"/>
                  <span className="text-[15px]">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button onClick={() => scrollTo('quote')} className="bg-[#0a2540] hover:bg-[#0a2540]/90 text-white rounded-lg h-12 px-7 text-[15px] font-semibold group">
                Get a Free Quote
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-0.5 transition-transform"/>
              </Button>
              <Button onClick={() => scrollTo('how')} variant="outline" className="h-12 px-7 rounded-lg text-[15px] font-semibold border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-[#0a2540]">
                How it Works
              </Button>
            </div>

            <div className="mt-10 flex items-center gap-4">
              <div className="flex">
                {Array.from({length:5}).map((_,i)=>(
                  <Star key={i} size={16} className="text-amber-500 fill-amber-500"/>
                ))}
              </div>
              <span className="text-sm text-slate-600">Rated <strong className="text-[#0a2540]">4.9/5</strong> by 2,400+ UK businesses</span>
            </div>
          </div>

          <div className="lg:col-span-6 relative fade-up" style={{ animationDelay: '150ms' }}>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10 border border-slate-200">
              <img
                src="https://images.pexels.com/photos/4175028/pexels-photo-4175028.jpeg"
                alt="Business funding partnership"
                className="w-full h-[520px] object-cover"
                loading="eager"
              />
              <div className="absolute left-6 bottom-6 right-6 bg-white/95 backdrop-blur-md rounded-2xl p-5 border border-slate-200 shadow-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-widest">Advance Available</div>
                    <div className="mt-1 text-3xl font-bold text-[#0a2540]">£50,000</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500 uppercase tracking-widest">Est. Repayment</div>
                    <div className="mt-1 text-lg font-semibold text-teal-600">10% of daily takings</div>
                  </div>
                </div>
                <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full w-[72%] bg-gradient-to-r from-teal-400 to-teal-600"/>
                </div>
                <div className="mt-2 flex justify-between text-xs text-slate-500">
                  <span>Approval: 72% complete</span>
                  <span className="text-teal-600 font-medium">On track</span>
                </div>
              </div>
            </div>

            <div className="absolute -left-4 top-8 bg-white rounded-2xl border border-slate-200 shadow-lg p-4 hidden md:block">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-teal-100 flex items-center justify-center">
                  <Sparkles size={20} className="text-teal-600"/>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Approval in</div>
                  <div className="text-sm font-semibold text-[#0a2540]">24 hours</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-slate-200 pt-10">
          {trustBadges.map((t) => (
            <div key={t.label} className="text-left">
              <div className="text-3xl md:text-4xl font-bold text-[#0a2540] tracking-tight">{t.label}</div>
              <div className="text-sm text-slate-500 mt-1">{t.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
